import {defineStore} from "pinia"
import {loginApi, type LoginData, loginWithGoogle} from "@/api/auth";
import {computed, ref} from "vue";
import type {IPermission, IUserDetail} from "@/interfaces/user";
import {showUser} from "@/api/user";
import * as Sentry from "@sentry/vue";

export const currentUserStoreName = "current-user";
export const useCurrentUserStore = defineStore(currentUserStoreName, () => {
    const user = ref<IUserDetail>()
    const token = ref<string>()
    const loading = ref<boolean>(false)

    const isLoggedIn = computed(() => !!user.value?.email)
    const allPermissions = computed<IPermission[]>(() => {
      const allPermissions: IPermission[] = []
      if (user.value?.permissions) {
        allPermissions.push(...user.value?.permissions)
      }

      if (user.value?.roles) {
        user.value.roles.forEach(role => {
          allPermissions.push(...role.permissions)
        })
      }

      return allPermissions
    })

    async function login(payload: LoginData) {
      try {
        loading.value = true;
        const loginData = await loginApi(payload);
        user.value = loginData.data.user;
        return true;
      } catch (e) {
        return false;
      } finally {
        addUserContextToSentry()
        loading.value = false;
      }
    }

    async function googleLogin(googleToken: string, invitationToken?: string) {
      try {
        loading.value = true;
        const loginData = await loginWithGoogle(googleToken, invitationToken);
        user.value = loginData.data.user
        return true;
      } catch (e) {
        return false;
      } finally {
        addUserContextToSentry()
        loading.value = false;
      }
    }

    async function refreshUser() {
      try {
        loading.value = true;
        const {data} = await showUser(Number(user.value?.id))
        user.value = data
        return true;
      } catch (e) {
        return false;
      } finally {
        addUserContextToSentry()
        loading.value = false;
      }
    }

    function $reset() {
      user.value = undefined
      token.value = ''
      loading.value = false
    }

    function addUserContextToSentry() {
      if (user.value) {
        Sentry.getCurrentScope().setUser({
          id: user.value.id,
          email: user.value.email,
          name: user.value?.name,
          company_id: user.value?.company_id,
          company_name: user.value?.company?.name
        })
      }
    }

    return {
      user,
      token,
      loading,
      isLoggedIn,
      googleLogin,
      login,
      $reset,
      allPermissions,
      refreshUser,
    }
  },
  {
    // @ts-ignore
    persist: true,
  }
);
